import {PATIENT_CONTEXT_TYPE} from '../../../../util/patientContextUtil';
import _ from 'lodash';
import {ControlDiagnosis, ControlInvestigations, ControlTreatment} from '../../../../controls/agyle/diagnosis';
import React, {useEffect, useState} from 'react';
import {getSafeguardingValues} from '../../forms/formsUtil';
import {Safeguarding} from '../../../../controls/agyle/ecds';
import {useTranslation} from 'react-i18next';
import Notes from '../../../../util/notes';
import {getConfig} from '../../../../util/configUtil';
import ICd10Control from '../../../../controls/agyle/icd10Control';
import {useSelector} from 'react-redux';
import {ctxPatientType} from '../../../../store/reducers/patientContext';

const MainScreenCoding = ({patient, modifyValues, disableSave}) => {
    const {t} = useTranslation();

    const patientType = useSelector(ctxPatientType);

    const [cancel, setCancel] = React.useState(false);
    const [update, setUpdate] = React.useState(2);
    const [loadingState, setLoadingState] = useState(false);

    // Configs
    const confirmDeleteDiagTreatInvest = getConfig('confirmDeleteDiagTreatInvest', false);
    const enableDiagTreatInvestWaitAfterChange = getConfig('enableDiagTreatInvestWaitAfterChange', false);
    const isMainScreenCustomInputEnabled = getConfig('mainScreenCustomizedInputs.enabled', false);
    const mainScreenCustomInputTreatment = getConfig('mainScreenCustomizedInputs.inputs.treatment', false);
    const mainScreenCustomInputInvestigations = getConfig('mainScreenCustomizedInputs.inputs.investigations', false);

    useEffect(() => {
        if (!cancel) setUpdate(update + 1);
        setCancel(false);
    }, [patient]);

    useEffect(() => {
        if (loadingState) {
            setTimeout(() => {
                setLoadingState(false);
            }, 1500);
        }
    }, [loadingState]);

    const getSafeguardingNote = (value) => {
        return Notes.getSafeguardingChangeNote(value);
    };

    const isTreatmentCustomized = () => isMainScreenCustomInputEnabled && mainScreenCustomInputTreatment;
    const isInvestigationsCustomized = () => isMainScreenCustomInputEnabled && mainScreenCustomInputInvestigations;

    const getEmergencyDepartmentCoding = () => {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                <ControlDiagnosis
                    label={t('ECDS Diagnosis')}
                    patient={patient}
                    color="tertiary"
                    agyleRefresh
                    paperBackground
                    bigLabel
                    deleteEntries
                    update={update}
                    clearIcon={null}
                    popupIcon={null}
                    endAdornment={null}
                    disabled={disableSave || patientType === PATIENT_CONTEXT_TYPE.EDAU}
                    disableEditTable={disableSave || patientType === PATIENT_CONTEXT_TYPE.EDAU}
                    value={
                        patient?.diagnosis?.primary
                            ? patient.diagnosis.primary
                            : []
                    }
                    onChange={(value) => {
                        setCancel(true);
                        let clone = _.cloneDeep(patient?.diagnosis);
                        if (!clone) {
                            clone = {};
                        }
                        clone.primary = value;
                        modifyValues([{name: 'diagnosis', value: clone}]);
                        setLoadingState(enableDiagTreatInvestWaitAfterChange);
                    }}
                    confirmDelete={confirmDeleteDiagTreatInvest}
                    loadingState={loadingState}
                    admitDate={patient?.admission?.startDate}
                />

                <ControlTreatment
                    label={t('ECDS Treatment')}
                    patient={patient}
                    color={'tertiary'}
                    update={update}
                    agyleRefresh
                    paperBackground
                    bigLabel
                    deleteEntries
                    clearIcon={null}
                    popupIcon={null}
                    showIndicatedFlag={isTreatmentCustomized() ? 'forMAIN' : ''}
                    disableCloseOnSelect
                    showAll={isTreatmentCustomized}
                    disabled={disableSave || patientType === PATIENT_CONTEXT_TYPE.EDAU}
                    disableEditTable={disableSave || patientType === PATIENT_CONTEXT_TYPE.EDAU}
                    value={
                        patient &&
                        patient.triage &&
                        patient.triage.treatmentAndManagementPlan
                            ? patient.triage.treatmentAndManagementPlan
                            : []
                    }
                    onChange={(value) => {
                        setCancel(true);
                        let clone = _.cloneDeep(patient?.triage);
                        if (!clone) {
                            clone = {};
                        }
                        clone.treatmentAndManagementPlan = value;
                        modifyValues([{name: 'triage', value: clone}]);
                        setLoadingState(enableDiagTreatInvestWaitAfterChange);

                    }}
                    confirmDelete={confirmDeleteDiagTreatInvest}
                    loadingState={loadingState}
                    admitDate={patient?.admission?.startDate}
                />

                <ControlInvestigations
                    label={t('ECDS Investigations')}
                    patient={patient}
                    color={'tertiary'}
                    update={update}
                    agyleRefresh
                    paperBackground
                    bigLabel
                    deleteEntries
                    clearIcon={null}
                    popupIcon={null}
                    showIndicatedFlag={isInvestigationsCustomized() ? 'forMAIN' : ''}
                    disableCloseOnSelect
                    showAll={isInvestigationsCustomized}
                    disabled={disableSave || patientType === PATIENT_CONTEXT_TYPE.EDAU}
                    disableEditTable={disableSave || patientType === PATIENT_CONTEXT_TYPE.EDAU}
                    value={
                        patient &&
                        patient.triage &&
                        patient.triage.investigations
                            ? patient.triage.investigations
                            : []
                    }
                    onChange={(value) => {
                        let clone = _.cloneDeep(patient?.triage);

                        setCancel(true);

                        if (!clone) {
                            clone = {};
                        }
                        clone.investigations = value;
                        modifyValues([{name: 'triage', value: clone}]);
                        setLoadingState(enableDiagTreatInvestWaitAfterChange);
                    }}
                    confirmDelete={confirmDeleteDiagTreatInvest}
                    loadingState={loadingState}
                    admitDate={patient?.admission?.startDate}
                />

                <Safeguarding
                    disabled={disableSave || patientType === PATIENT_CONTEXT_TYPE.EDAU}
                    label={t('ECDS Safeguarding')}
                    color="primary"
                    agyleRefresh
                    paperBackground
                    bigLabel
                    clearIcon={null}
                    popupIcon={null}
                    value={getSafeguardingValues(patient)}
                    onChange={(value) => {
                        modifyValues([
                            {
                                name: 'studies.mainScreenSafeguarding',
                                value: value,
                                noteToAdd: getSafeguardingNote(value),
                            },
                        ]);
                    }}
                    admitDate={patient?.admission?.startDate}
                />
            </div>
        );
    };

    const getInPatientCoding = () => {
        return (
            <ICd10Control
                value={patient?.icd || []}
                disabled={disableSave}
                onChange={(value) => {
                    setCancel(true);
                    modifyValues([{
                        name: "icd",
                        value,
                        noteToAdd: Notes.getICD10(value)
                    }]);
                }}
            />
        )
    };

    const getCoding = () => {
        switch (patientType) {
            case PATIENT_CONTEXT_TYPE.EDAU:
            case PATIENT_CONTEXT_TYPE.ED:
            case PATIENT_CONTEXT_TYPE.SDEC:
            case PATIENT_CONTEXT_TYPE.FAST_REGISTRY:
            case PATIENT_CONTEXT_TYPE.TEMPORARY:
                return getEmergencyDepartmentCoding();
            case PATIENT_CONTEXT_TYPE.INPATIENT:
                return getInPatientCoding();
            default:
                return null;
        }
    };

    return (
        <>
            {getCoding()}
        </>
    );
};

function areEqual(prevProps, nextProps) {
    let isEqual = _.isEqual(prevProps.patient, nextProps.patient);
    isEqual = isEqual && _.isEqual(prevProps.disableSave, nextProps.disableSave);
    return isEqual;
}

export default React.memo(MainScreenCoding, areEqual);